import {useId, useState} from 'react';
import {formatDistanceToNow} from 'date-fns';
import {Pencil, Plus, Trash2} from 'lucide-react';
import {
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Field,
  FieldLabel,
  Spinner,
  Textarea
} from '@kroll-resolver-crisp/ui-components-react';
import {
  useSubmissionComments,
  useCreateSubmissionComment,
  useUpdateSubmissionComment,
  useDeleteSubmissionComment
} from '@/api/hooks';
import {type SubmissionComment} from '@/api/submissionComments';
import {getApiErrorMessage} from '@/api/client';
import {useConfirm} from '@/components/shared/useConfirm';
import {useAuth} from '@/context/useAuth';

// The submission's expanded panel, not its timeline: a reviewer's comment is content written about
// the submission, not a step in its delivery lifecycle. Writing is BroadView-only server-side, so
// the compose box is hidden rather than disabled for everyone else - an analyst reads the thread.
export function SubmissionCommentsSection({
  submissionId,
  onAdded,
  onLastDeleted
}: {
  readonly submissionId: string;
  // Lets the table row's "has reviewer comments" badge appear immediately - the comment thread this
  // section reads is a separate query the table's own row data never touches.
  readonly onAdded?: () => void;
  // Fired when a delete empties the thread, so the row badge can clear immediately too - otherwise
  // it stays lit until the next full list refetch, saying there's a comment that no longer exists.
  readonly onLastDeleted?: () => void;
}) {
  // Read here rather than threaded down from SubmissionsWorkspace: the role gate and the
  // is-this-mine check are this component's business, and nothing between here and there wants them.
  const {user} = useAuth();
  const canComment = user?.role === 'analyst_admin' || user?.role === 'admin';
  // AuthUser.username is the email - the API mints the claim from it (see ICurrentUser.Email).
  const currentUserEmail = user?.username ?? '';

  const {data, isLoading} = useSubmissionComments(submissionId);
  const create = useCreateSubmissionComment(submissionId);
  const update = useUpdateSubmissionComment(submissionId);
  const remove = useDeleteSubmissionComment(submissionId);

  const [body, setBody] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingBody, setEditingBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const composeId = useId();
  const {confirm, confirmDialog} = useConfirm();

  const comments = data ?? [];

  async function handleCreate() {
    const trimmed = body.trim();
    if (trimmed === '') {
      return;
    }

    setError(null);
    try {
      await create.mutateAsync({body: trimmed});
      setBody('');
      onAdded?.();
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Could not add the comment.'));
    }
  }

  async function handleSaveEdit(comment: SubmissionComment) {
    const trimmed = editingBody.trim();
    if (trimmed === '') {
      return;
    }

    setError(null);
    try {
      await update.mutateAsync({id: comment.id, body: trimmed});
      setEditingId(null);
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Could not save the comment.'));
    }
  }

  async function handleDelete(id: string) {
    if (!(await confirm({title: 'Delete this comment?', description: 'This cannot be undone.'}))) {
      return;
    }

    setError(null);
    const wasLast = comments.length === 1;
    try {
      await remove.mutateAsync(id);
      if (wasLast) {
        onLastDeleted?.();
      }
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Could not delete the comment.'));
    }
  }

  return (
    <div className="space-y-2">
      {isLoading ? (
        <div className="text-muted-foreground flex items-center gap-2 text-xs">
          <Spinner />
          Loading comments…
        </div>
      ) : comments.length === 0 ? (
        <Empty variant="outline" className="gap-2 p-4">
          <EmptyHeader className="gap-0.5">
            <EmptyTitle>No reviewer comments</EmptyTitle>
            <EmptyDescription>Nobody has left a comment on this submission yet.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        // Plain ruled blocks rather than a Card per comment: the sheet is a popover surface, and a
        // card on it is all but invisible (1.07:1 in dark), so the rule is what separates entries.
        <ul>
          {comments.map((comment) => (
            <li key={comment.id} className="border-border space-y-1 border-t py-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium">
                  {comment.author_email.split('@')[0] ?? comment.author_email}
                </span>
                <span className="text-muted-foreground text-xs">
                  {formatDistanceToNow(new Date(comment.created_at), {addSuffix: true})}
                  {comment.edited_at ? ' · edited' : ''}
                </span>
                {comment.author_email === currentUserEmail ? (
                  <span className="ml-auto flex items-center gap-0.5">
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label="Edit comment"
                      className="text-muted-foreground"
                      onClick={() => {
                        setEditingId(comment.id);
                        setEditingBody(comment.body);
                      }}>
                      <Pencil />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label="Delete comment"
                      className="text-muted-foreground hover:text-destructive-ink"
                      onClick={() => {
                        void handleDelete(comment.id);
                      }}>
                      <Trash2 />
                    </Button>
                  </span>
                ) : null}
              </div>
              {editingId === comment.id ? (
                <div className="space-y-1.5">
                  <Textarea
                    aria-label="Edit comment body"
                    value={editingBody}
                    rows={3}
                    onChange={(event) => {
                      setEditingBody(event.target.value);
                    }}
                  />
                  <div className="flex gap-1.5">
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => {
                        setEditingId(null);
                      }}>
                      Cancel
                    </Button>
                    <Button
                      size="xs"
                      disabled={update.isPending}
                      onClick={() => {
                        void handleSaveEdit(comment);
                      }}>
                      Save
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-xs whitespace-pre-wrap">{comment.body}</p>
              )}
            </li>
          ))}
        </ul>
      )}

      {canComment ? (
        <Field>
          <FieldLabel htmlFor={composeId} className="sr-only">
            New comment
          </FieldLabel>
          <Textarea
            id={composeId}
            value={body}
            rows={2}
            className="min-h-0"
            placeholder="Add a comment for the analyst…"
            onChange={(event) => {
              setBody(event.target.value);
            }}
          />
          <div className="flex justify-end">
            <Button
              size="sm"
              disabled={create.isPending || body.trim() === ''}
              onClick={() => {
                void handleCreate();
              }}>
              {create.isPending ? <Spinner /> : <Plus />}
              {create.isPending ? 'Adding…' : 'Add comment'}
            </Button>
          </div>
        </Field>
      ) : null}

      {error ? <p className="text-destructive-ink text-xs">{error}</p> : null}
      {confirmDialog}
    </div>
  );
}
